import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import * as Progress from '@radix-ui/react-progress';

import { RecentPurchase } from './RecentPurchase';

import { getOrdersV2 } from 'common/api/e-comm/orders/service';
import { useApi } from 'common/modules/async-actions/useApi';
import { Order } from 'common/api/e-comm/models/Order';
import { createStyles } from 'utils/createStyle';

type PurchaseHistoryProps = { startDate: Date; endDate: Date };

export const PurchaseHistory = ({ startDate, endDate }: PurchaseHistoryProps) => {
    const [fetchOrders, { loading }, orders] = useApi(getOrdersV2);

    useEffect(() => {
        // NOTE: we'll want to cache this in the context
        const startDateStr = startDate.toISOString().split('T')[0];
        let endDateStr = endDate.toISOString().split('T')[0];

        // if dates are the same, add 1 day to the end date to retrieve all orders for that day.
        if (startDateStr === endDateStr) {
            endDate.setDate(endDate.getDate() + 1);
            endDateStr = endDate.toISOString().split('T')[0];
        }

        fetchOrders(startDateStr, endDateStr);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);

    return (
        <div>
            <PurchaseHistoryList loading={loading} orders={orders?.data ?? []} />
        </div>
    );
};

interface PurchaseHistoryListProps {
    orders: Order[];
    loading: boolean;
}

const RESULTS_PER_LOAD = 50;
const RESULTS_INITIAL_LOAD = 20;

const styles = createStyles({
    loadMore: [
        'py-2 px-10 rounded-3xl bg-brand text-white font-semibold mt-8',
        { fontSize: 18, lineHeight: '20px' },
    ],
    totalText: ['text-grey-1', { fontSize: 14, lineHeight: '22px' }],
    indicator: ['rounded-3xl bg-bonus-pink h-2'],
    root: ['rounded-3xl bg-grey-5 h-2 inline-block'],
    loading: ['w-full mb-4', { height: '12rem' }],
});

const PurchaseHistoryList = ({ orders, loading }: PurchaseHistoryListProps) => {
    const [pageSize, setPageSize] = useState(RESULTS_PER_LOAD);

    const loadMore = useCallback(() => {
        setPageSize(pageSize + RESULTS_PER_LOAD);
    }, [pageSize, setPageSize]);

    const total = useMemo(() => orders.length, [orders]);
    const to = useMemo(() => Math.min(pageSize, total), [pageSize, total]);

    useEffect(() => {
        setPageSize(RESULTS_INITIAL_LOAD);
    }, [orders, setPageSize]);

    if (loading) {
        return (
            <>
                <Skeleton className={styles.loading} />
                <Skeleton className={styles.loading} />
                <Skeleton className={styles.loading} />
            </>
        );
    }

    return (
        <div>
            {orders.slice(0, to).map((order: Order) => (
                <div key={order.orderNumber} className="mb-5">
                    <RecentPurchase order={order} />
                </div>
            ))}
            {total > 0 && (
                <div className="text-center mt-16">
                    <p className={styles.totalText}>
                        Viewing <span className="font-bold text-brand">{to}</span> of{' '}
                        <span className="font-bold text-brand">{total}</span> Results
                    </p>
                    <Progress.Root
                        className={styles.root}
                        style={{ width: 180 }}
                        value={to}
                        max={total}
                    >
                        <Progress.Indicator
                            className={styles.indicator}
                            style={{ width: `${(to / total) * 100}%` }}
                        />
                    </Progress.Root>
                    <div>
                        {to < total && (
                            <button className={styles.loadMore} onClick={loadMore}>
                                Load More Items
                            </button>
                        )}
                    </div>
                </div>
            )}
            {total <= 0 && <div>No orders found.</div>}
        </div>
    );
};
